// Visar hur dokumentet hänger ihop med omvärlden: aktiva delningslänkar och
// kopior i SharePoint/OneDrive – samt vad som händer vid en borttagning.
import React from "react";
import { Cloud, Link2, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export interface SyncStatusTarget {
  projectId?: string | null;
  documentId: string;
  documentType?: string;
  docNumber?: string;
}

export interface SyncStatusInfo {
  activeShares: number;
  recipients: string[];
  destinationLabel: string | null;
  destinationPath: string | null;
  lastSyncAt: string | null;
}

export async function fetchSyncStatus(target: SyncStatusTarget): Promise<SyncStatusInfo> {
  const empty: SyncStatusInfo = {
    activeShares: 0,
    recipients: [],
    destinationLabel: null,
    destinationPath: null,
    lastSyncAt: null,
  };
  if (!target.projectId) return empty;

  const [sharesRes, destRes, logRes] = await Promise.all([
    supabase
      .from("document_shares")
      .select("recipient_email, recipient_name")
      .eq("project_id", target.projectId)
      .eq("document_id", target.documentId)
      .is("revoked_at", null),
    supabase
      .from("document_destinations")
      .select("label, connector_type, root_path, last_sync_at")
      .eq("project_id", target.projectId)
      .eq("is_active", true)
      .order("updated_at", { ascending: false })
      .limit(1),
    supabase
      .from("document_sync_log")
      .select("synced_at, status, filename")
      .eq("project_id", target.projectId)
      .eq("status", "success")
      .order("synced_at", { ascending: false })
      .limit(1),
  ]);

  const shares = (sharesRes.data ?? []) as { recipient_email?: string; recipient_name?: string }[];
  const dest = (destRes.data ?? [])[0] as
    | { label?: string; connector_type?: string; root_path?: string; last_sync_at?: string }
    | undefined;
  const log = (logRes.data ?? [])[0] as { synced_at?: string } | undefined;

  return {
    activeShares: shares.length,
    recipients: shares.map((s) => s.recipient_name || s.recipient_email || "").filter(Boolean),
    destinationLabel: dest
      ? dest.label || (dest.connector_type === "onedrive" ? "OneDrive" : "SharePoint")
      : null,
    destinationPath: dest?.root_path ?? null,
    lastSyncAt: log?.synced_at ?? dest?.last_sync_at ?? null,
  };
}

const formatDate = (value: string) =>
  new Date(value).toLocaleString("sv-SE", { dateStyle: "medium", timeStyle: "short" });

interface Props {
  target: SyncStatusTarget;
  /** Visar även konsekvenstexten för borttagning (används i raderingsrutan) */
  showDeletionImpact?: boolean;
  /** Uppdatera när detta värde ändras (t.ex. realtidsstämpel) */
  refreshKey?: unknown;
  className?: string;
}

export const DocumentSyncStatus: React.FC<Props> = ({
  target,
  showDeletionImpact,
  refreshKey,
  className = "",
}) => {
  const [info, setInfo] = React.useState<SyncStatusInfo | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    fetchSyncStatus(target)
      .then((res) => active && setInfo(res))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.projectId, target.documentId, refreshKey]);

  if (loading) {
    return (
      <p className={`flex items-center gap-2 text-xs text-muted-foreground ${className}`}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Hämtar synkstatus…
      </p>
    );
  }

  if (!info) return null;
  if (info.activeShares === 0 && !info.destinationLabel && !showDeletionImpact) return null;

  return (
    <div className={`space-y-1.5 rounded-lg border border-border/60 bg-muted/40 p-3 ${className}`}>
      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <Link2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          {info.activeShares === 0 ? (
            "Inga aktiva delningslänkar för det här dokumentet."
          ) : (
            <>
              <span className="font-medium text-foreground">
                {info.activeShares} aktiv{info.activeShares === 1 ? " länk" : "a länkar"}
              </span>
              {info.recipients.length > 0 && ` (${info.recipients.slice(0, 3).join(", ")}${
                info.recipients.length > 3 ? " m.fl." : ""
              })`}
              {showDeletionImpact &&
                ` – spärras vid borttagning. Mottagaren ser "Dokumentet är borttaget av avsändaren".`}
            </>
          )}
        </span>
      </p>

      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <Cloud className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          {info.destinationLabel ? (
            <>
              Synkas mot <span className="font-medium text-foreground">{info.destinationLabel}</span>
              {info.destinationPath ? ` (${info.destinationPath})` : ""}
              {info.lastSyncAt ? ` – senast ${formatDate(info.lastSyncAt)}` : " – ingen synk ännu"}.
              {showDeletionImpact &&
                " Kopian hos kunden ligger kvar som en egen fil även efter borttagning här."}
            </>
          ) : (
            "Ingen koppling till SharePoint/OneDrive i det här projektet."
          )}
        </span>
      </p>
    </div>
  );
};

export default DocumentSyncStatus;
